import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'

import type { HylyghtActivity } from '@healthblocks-io/core'
import type { CareplanActivityWithCareplan } from '@/modules/careplan/hooks/useCareplanActivity'

/**
 * Create Hylyght careplan activities based on Hylyght activity
 * If the activity has an interval, create multiple careplan activities )
 * We only create careplans for the completed and scheduled activities
 * @param originalCareplanActivity - The original careplan activity
 * @returns
 */
export function generateHylyghtCareplanActivitiesBasedOnInterval(
  originalCareplanActivity: CareplanActivityWithCareplan,
): CareplanActivityWithCareplan[] {
  if (originalCareplanActivity.type !== 'Hylyght' || !originalCareplanActivity.meta.hylyghtActivity?.interval) {
    return [originalCareplanActivity]
  }

  const hylyghtActivity = originalCareplanActivity.meta.hylyghtActivity
  const { interval, completedDates: hylyghtCompleteDates } = hylyghtActivity

  const today = new Date()

  // Remove duplicates from completed dates
  const completedDates = [...new Set(hylyghtCompleteDates)]

  const occurrences: CareplanActivityWithCareplan[] = []

  if (completedDates) {
    switch (interval) {
      case 'daily': {
        // If the activity is completed, create a careplan activity for each completion date
        completedDates.forEach((date, index) => {
          const isActive = isWithinInterval(today, {
            start: startOfDay(new Date(date)),
            end: endOfDay(new Date(date)),
          })

          occurrences.push({
            ...originalCareplanActivity,
            status: 'completed',
            id: isActive ? originalCareplanActivity.id : originalCareplanActivity.id + (index + 1), // mock id for completed activities to prevent duplicates (this activity is purely for history)
            plannedAt: format(new Date(date), 'yyyy-MM-dd HH:mm:ss'),
            endAt: format(endOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss'),
          })
        })

        const activityForTodayIsCompleted = occurrences.some((occurrence) =>
          isWithinInterval(today, {
            start: startOfDay(new Date(occurrence.plannedAt)),
            end: endOfDay(new Date(occurrence.plannedAt)),
          }),
        )

        // If the current day is not completed, add the current day
        if (!activityForTodayIsCompleted) {
          occurrences.push({
            ...originalCareplanActivity,
            status: 'scheduled',
            id: originalCareplanActivity.id,
            plannedAt: format(today, 'yyyy-MM-dd HH:mm:ss'),
            endAt: format(endOfDay(today), 'yyyy-MM-dd HH:mm:ss'),
          })
        }
        break
      }
      case 'weekly': {
        // If the activity is completed, create a careplan activity for each completion date
        completedDates.forEach((date, index) => {
          const isActive = isWithinInterval(today, {
            start: startOfWeek(new Date(date)),
            end: endOfWeek(new Date(date)),
          })

          occurrences.push({
            ...originalCareplanActivity,
            status: 'completed',
            id: isActive ? originalCareplanActivity.id : originalCareplanActivity.id + (index + 1), // mock id for completed activities to prevent duplicates (this activity is purely for history)
            plannedAt: format(new Date(date), 'yyyy-MM-dd HH:mm:ss'),
            endAt: format(endOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss'),
          })
        })

        const activityForThisWeekIsCompleted = occurrences.some((occurrence) =>
          isWithinInterval(today, {
            start: startOfWeek(new Date(occurrence.plannedAt)),
            end: endOfWeek(new Date(occurrence.plannedAt)),
          }),
        )

        // If the current week is not completed, add the current week
        if (!activityForThisWeekIsCompleted) {
          occurrences.push({
            ...originalCareplanActivity,
            status: 'scheduled',
            id: originalCareplanActivity.id,
            plannedAt: format(today, 'yyyy-MM-dd HH:mm:ss'),
            endAt: format(endOfWeek(today), 'yyyy-MM-dd HH:mm:ss'),
          })
        }

        break
      }

      case 'monthly': {
        // If the activity is completed, create a careplan activity for each completion date
        completedDates.forEach((date, index) => {
          // Check if the activity is active for this month
          const isActive = isWithinInterval(today, {
            start: startOfMonth(new Date(date)),
            end: endOfMonth(new Date(date)),
          })
          occurrences.push({
            ...originalCareplanActivity,
            status: 'completed',
            id: isActive ? originalCareplanActivity.id : originalCareplanActivity.id + (index + 1), // mock id for completed activities to prevent duplicates (this activity is purely for history)
            plannedAt: format(new Date(date), 'yyyy-MM-dd HH:mm:ss'),
            endAt: format(endOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss'),
          })
        })

        const activityForThisMonthIsCompleted = occurrences.some((occurrence) =>
          isWithinInterval(today, {
            start: startOfMonth(new Date(occurrence.plannedAt)),
            end: endOfMonth(new Date(occurrence.plannedAt)),
          }),
        )

        // If the current month is not completed, add the current month
        if (!activityForThisMonthIsCompleted) {
          occurrences.push({
            ...originalCareplanActivity,
            status: 'scheduled',
            id: originalCareplanActivity.id,
            plannedAt: format(today, 'yyyy-MM-dd HH:mm:ss'),
            endAt: format(endOfMonth(today), 'yyyy-MM-dd HH:mm:ss'),
          })
        }
        break
      }
    }
  }

  return occurrences
}

/**
 * Helper function to check if a Hylyght activity is completed for today
 * @param careplanActivity - The careplan activity
 * @returns Whether the Hylyght activity is completed for today
 */
export function isHylyghtActivityCompleted(hylyghtActivity: HylyghtActivity): boolean {
  if (!hylyghtActivity) {
    return false
  }

  if (!hylyghtActivity.completedDates) {
    return false
  }

  const today = new Date()

  return hylyghtActivity.completedDates.some((date) => {
    let startDate: Date
    let endDate: Date

    switch (hylyghtActivity.interval) {
      case 'daily': {
        startDate = startOfDay(new Date(date))
        endDate = endOfDay(new Date(date))
        break
      }
      case 'weekly': {
        startDate = startOfWeek(new Date(date))
        endDate = endOfWeek(new Date(date))
        break
      }
      case 'monthly': {
        startDate = startOfMonth(new Date(date))
        endDate = endOfMonth(new Date(date))
        break
      }
      default: {
        return false
      }
    }

    return isWithinInterval(today, {
      start: startDate,
      end: endDate,
    })
  })
}

/**
 * Helper function to check if a user can open the Hylyght activity
 * In case of a questionnaire, the user can open the Hylyght activity if the questionnaire if the questionnaire is in the current interval
 */
export function canOpenHylyghtActivity(careplanActivity: CareplanActivityWithCareplan): boolean {
  if (!careplanActivity.meta.hylyghtActivity) {
    console.log('No Hylyght activity found')
    return false
  }

  if (careplanActivity.meta.hylyghtActivity.type === 'program') {
    return true
  }

  const hylyghtActivity = careplanActivity.meta.hylyghtActivity

  switch (hylyghtActivity.interval) {
    case 'daily': {
      return isWithinInterval(new Date(), {
        start: startOfDay(new Date(careplanActivity.plannedAt)),
        end: endOfDay(new Date(careplanActivity.plannedAt)),
      })
    }
    case 'weekly': {
      return isWithinInterval(new Date(), {
        start: startOfWeek(new Date(careplanActivity.plannedAt)),
        end: endOfWeek(new Date(careplanActivity.plannedAt)),
      })
    }
    case 'monthly': {
      return isWithinInterval(new Date(), {
        start: startOfMonth(new Date(careplanActivity.plannedAt)),
        end: endOfMonth(new Date(careplanActivity.plannedAt)),
      })
    }
    default: {
      return false
    }
  }
}
